import { ApolloError, useQuery } from '@apollo/client';
import { createError } from '@common/neo.tax';

import {
  GET_GAAP_ONBOARDING_FLOW_STATUS,
  GetGaapOnboardingFlowStatusQuery,
  GetGaapOnboardingFlowStatusQueryVariables,
} from '../graphql';
import { useCurrentCompanyId } from './useCurrentCompanyId';

type UseOnboardingFlowCompletionPercentProps = {
  pollIntervalMs?: number;
};

type UseOnboardingFlowCompletionPercentResult = {
  startPolling: (pollIntervalMs: number) => void;
  stopPolling: () => void;
} & (
  | {
      percentCompleted: number;
      loading: false;
      error: undefined;
    }
  | {
      percentCompleted: null;
      loading: true;
      error: undefined;
    }
  | {
      percentCompleted: null;
      loading: false;
      error: ApolloError;
    }
);

export const useOnboardingFlowCompletionPercent = (
  props: UseOnboardingFlowCompletionPercentProps = {},
): UseOnboardingFlowCompletionPercentResult => {
  const { pollIntervalMs } = {
    pollIntervalMs: 0,
    ...props,
  };

  const companyId = useCurrentCompanyId();

  const { data, error, loading, startPolling, stopPolling } = useQuery<
    GetGaapOnboardingFlowStatusQuery,
    GetGaapOnboardingFlowStatusQueryVariables
  >(GET_GAAP_ONBOARDING_FLOW_STATUS, { variables: { companyId }, pollInterval: pollIntervalMs });

  const baseResult = {
    startPolling,
    stopPolling,
  };

  if (error) {
    return { ...baseResult, percentCompleted: null, loading: false, error };
  }
  if (loading) {
    return { ...baseResult, percentCompleted: null, loading: true, error: undefined };
  }
  if (data) {
    return {
      ...baseResult,
      percentCompleted: data.getGaapOnboardingFlowStatus.percentCompleted,
      loading: false,
      error: undefined,
    };
  }
  throw createError(
    'UnexpectedQueryResponse',
    'Query returned no data, no error, and not loading. This should never happen.',
  );
};
